/**
 * Multi-input JSON editor for the sandbox.
 *
 * Renders a list of raw-JSON text items; each valid item becomes one task when
 * the sandbox fans out submission. Mirrors the add/remove item pattern of
 * SchemaForm's ArrayField and the live parse/validate of its RawJsonField, but
 * at whole-input granularity rather than per-field. A single item covers the
 * "author input as JSON" case; adding items covers "spawn N tasks at once".
 */
import { Textarea } from '../../ui/finixt.js';

export interface JsonItem {
  id: string;
  text: string;
}

export interface ParsedJsonItems {
  /** One parsed object per item; populated in order only when `allValid`. */
  inputs: Record<string, unknown>[];
  /** Per-item message, aligned with `items`; null when the item is valid. */
  errors: (string | null)[];
  allValid: boolean;
}

export interface JsonInputsEditorProps {
  items: JsonItem[];
  errors: (string | null)[];
  onChange: (next: JsonItem[]) => void;
}

let _seq = 0;

/** Keyed item factory — stable id without a secure-context `crypto` dependency. */
export function newJsonItem(text = ''): JsonItem {
  _seq += 1;
  return { id: `json-${_seq}`, text };
}

/** Parse every item to a JSON object, collecting per-item errors. Pure. */
export function parseJsonItems(items: JsonItem[]): ParsedJsonItems {
  const inputs: Record<string, unknown>[] = [];
  const errors: (string | null)[] = [];
  for (const item of items) {
    const text = item.text.trim();
    if (text === '') {
      errors.push('Input is empty.');
      continue;
    }
    try {
      const parsed: unknown = JSON.parse(text);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        errors.push('Top-level value must be a JSON object.');
        continue;
      }
      inputs.push(parsed as Record<string, unknown>);
      errors.push(null);
    } catch (err) {
      errors.push(err instanceof Error ? err.message : String(err));
    }
  }
  const allValid = items.length > 0 && errors.every((e) => e === null);
  return { inputs, errors, allValid };
}

export function JsonInputsEditor({ items, errors, onChange }: JsonInputsEditorProps) {
  function setText(id: string, text: string) {
    onChange(items.map((it) => (it.id === id ? { ...it, text } : it)));
  }
  function removeItem(id: string) {
    onChange(items.filter((it) => it.id !== id));
  }
  function addItem() {
    onChange([...items, newJsonItem('')]);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {items.map((item, i) => (
        <div
          key={item.id}
          style={{
            borderRadius: 'var(--radius-chip)',
            border: '1px solid var(--border-hairline)',
            background: 'var(--surface-card)',
            padding: 11,
          }}
        >
          <div
            style={{
              marginBottom: 7,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span
              style={{
                fontSize: 'var(--text-meta-lg)',
                fontWeight: 'var(--weight-medium)',
                color: 'var(--text-muted)',
              }}
            >
              input #{i + 1}
            </span>
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => removeItem(item.id)}
                style={{
                  borderRadius: 'var(--radius-chip)',
                  border: '1px solid var(--border-hairline)',
                  paddingLeft: 7,
                  paddingRight: 7,
                  paddingTop: 5,
                  paddingBottom: 5,
                  fontSize: 'var(--text-meta-lg)',
                  color: 'var(--text-muted)',
                }}
              >
                Remove
              </button>
            )}
          </div>
          <Textarea
            rows={6}
            spellCheck={false}
            value={item.text}
            onChange={(e) => setText(item.id, e.target.value)}
            className="mono"
            style={{ fontSize: 'var(--text-meta-lg)' }}
          />
          {errors[i] && (
            <p style={{ marginTop: 5, fontSize: 'var(--text-meta-lg)', color: 'var(--danger)' }}>
              {errors[i]}
            </p>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        style={{
          borderRadius: 'var(--radius-chip)',
          border: '1px dashed var(--border-hairline)',
          background: 'transparent',
          padding: '5px 11px',
          fontSize: 'var(--text-meta-lg)',
          color: 'var(--text-accent)',
          cursor: 'pointer',
        }}
      >
        + JSON
      </button>
    </div>
  );
}
